import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  ArrowLeft,
  ClipboardCheck,
  RefreshCcw,
  Sparkles,
  HelpCircle,
  Check,
  X,
  FileText,
  Play,
  Library,
} from "lucide-react";

import { api } from "@/api/client";
import { Navbar } from "@/components/layout/Nav";
import { Carlos, Ana, Mascot } from "@/components/characters/Characters";
import { SpeechBubble, FactBox } from "@/components/magazine/Panels";
import ShareComic from "@/components/ShareComic";
import PdfComicButton from "@/components/PdfComicButton";
import ComicBadge from "@/components/ComicBadge";
import { useLang } from "@/context/LangContext";
import { findComic, getInteractive, deriveScenes } from "@/data/comics";
import { materialsForComic } from "@/data/materials";

/* ─────────── Small helpers ─────────── */
const CharacterFor = ({ who, size = 96 }) => {
  if (who === "ana") return <Ana size={size} />;
  if (who === "carlos") return <Carlos size={size} waving />;
  return <Mascot size={size} />;
};

/* Comic-style sound-effect word (POW/ZAS/¡BAM!) that flies in on page turn */
const SFX = ({ word, color = "#FFD100" }) => (
  <motion.div
    key={word}
    initial={{ opacity: 0, scale: 0.4, rotate: -20 }}
    animate={{ opacity: 1, scale: 1, rotate: -8 }}
    exit={{ opacity: 0, scale: 0.4 }}
    transition={{ type: "spring", stiffness: 220, damping: 12 }}
    className="absolute top-3 right-3 pointer-events-none z-10 select-none"
  >
    <div
      className="brutal px-4 py-2 font-display font-black text-2xl sm:text-3xl uppercase tracking-widest"
      style={{ background: color, transform: "rotate(-6deg)" }}
    >
      {word}
    </div>
  </motion.div>
);
const SFX_WORDS = ["¡POW!", "¡ZAS!", "¡BAM!", "¡WOW!", "¡BOOM!", "¡GRACIAS!", "¡SIP!"];

/* ─────────── Interactive quiz ("¿Y ahora?") ─────────── */
const InteractiveBeat = ({ quiz, lang, onAnswered }) => {
  const [picked, setPicked] = useState(null);
  const [open, setOpen] = useState(false);
  const selected = picked != null ? quiz.choices[picked] : null;

  useEffect(() => {
    setPicked(null);
    setOpen(false);
  }, [quiz]);

  if (!open) {
    return (
      <div className="brutal p-4 bg-canales-yellow flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-display font-black text-lg">
          <HelpCircle className="w-5 h-5" /> {lang === "es" ? "¿Y ahora tú qué harías?" : "What would YOU do?"}
        </div>
        <button
          data-testid="comic-quiz-open"
          onClick={() => setOpen(true)}
          className="brutal-btn bg-canales-red text-white px-4 py-2 text-sm font-display font-black"
        >
          <Sparkles className="w-4 h-4 mr-1 inline" />
          {lang === "es" ? "Jugar la escena" : "Play the scene"}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="brutal p-5 bg-canales-cream"
      data-testid="comic-quiz"
    >
      <div className="font-display font-black text-lg mb-3 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-canales-red" />
        {quiz.prompt[lang]}
      </div>
      <div className="grid gap-2">
        {quiz.choices.map((c, i) => {
          const isPicked = picked === i;
          const showResult = picked != null;
          const state = !showResult
            ? "bg-white hover:bg-canales-yellow"
            : isPicked
            ? c.correct
              ? "bg-green-200"
              : "bg-red-200"
            : c.correct
            ? "bg-green-100"
            : "bg-white opacity-70";
          return (
            <button
              key={i}
              data-testid={`comic-quiz-choice-${i}`}
              disabled={showResult}
              onClick={() => {
                setPicked(i);
                onAnswered?.(c.correct);
              }}
              className={`text-left brutal-sm p-3 font-body flex items-start gap-2 ${state}`}
            >
              <span className="font-display font-black mr-1">{String.fromCharCode(65 + i)}.</span>
              <span className="flex-1">{c.text?.[lang] || c[lang]}</span>
              {showResult && isPicked && (c.correct ? (
                <Check className="w-5 h-5 text-green-700 flex-shrink-0" />
              ) : (
                <X className="w-5 h-5 text-red-700 flex-shrink-0" />
              ))}
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 brutal p-4 flex items-start gap-3 ${selected.correct ? "bg-green-50" : "bg-red-50"}`}
            data-testid="comic-quiz-feedback"
          >
            <CharacterFor who={selected.correct ? "carlos" : "ana"} size={64} />
            <div>
              <div className={`font-display font-black ${selected.correct ? "text-green-700" : "text-red-700"}`}>
                {selected.correct
                  ? lang === "es"
                    ? "¡Perfecto!"
                    : "Perfect!"
                  : lang === "es"
                  ? "Ojo…"
                  : "Watch out…"}
              </div>
              <p className="font-body text-sm mt-1">{selected.reaction[lang]}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─────────── One panel (character + bubble) ─────────── */
const Panel = ({ panel, lang, reverse = false }) => {
  const who = panel.speaker || "narrator";
  const isNarrator = who === "narrator";
  return (
    <div
      className="brutal p-4 sm:p-5 relative h-full"
      style={{ background: panel.bg || "#fff", transform: `rotate(${panel.tilt || 0}deg)` }}
    >
      {isNarrator ? (
        <div className="brutal-sm bg-canales-cream p-3 font-display font-bold text-base italic">
          {panel.text[lang]}
        </div>
      ) : (
        <div className={`flex gap-3 items-start ${reverse ? "flex-row-reverse" : ""}`}>
          <div className="shrink-0">
            <CharacterFor who={who} size={100} />
          </div>
          <div className="flex-1">
            <SpeechBubble from={who === "ana" ? "ana" : "carlos"}>
              {panel.text[lang]}
            </SpeechBubble>
            {panel.action && (
              <div className="mt-2 text-xs font-body italic text-neutral-600 border-l-4 border-black pl-3">
                {panel.action[lang]}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────── Cover page ─────────── */
const PageCover = ({ comic, lang, illustration }) => (
  <div
    className="brutal relative overflow-hidden text-center"
    style={{ background: comic.cover_color, color: "white", minHeight: "60vh" }}
    data-testid="comic-cover"
  >
    {illustration && (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/illustrations/${illustration.key}`}
        alt="cover"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
    )}
    <div
      className="absolute inset-0 opacity-25 pointer-events-none mix-blend-multiply"
      style={{
        backgroundImage: "radial-gradient(#000 1.5px, transparent 1.5px)",
        backgroundSize: "22px 22px",
      }}
    />
    <div className="relative p-8 sm:p-14">
      <div
        className="inline-block text-black px-4 py-1 border-4 border-black rounded-full font-display font-black tracking-widest uppercase text-sm"
        style={{ background: comic.accent }}
      >
        {lang === "es" ? "Cómic Canales" : "Canales Comic"}
      </div>
      <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl leading-[1.02] drop-shadow-[3px_3px_0_rgba(0,0,0,0.6)]">
        {comic.title[lang]}
      </h1>
      <p className="mt-4 font-display text-lg sm:text-2xl opacity-95 drop-shadow-[2px_2px_0_rgba(0,0,0,0.5)]">
        {comic.subtitle[lang]}
      </p>
      {!illustration && (
        <div className="mt-8 flex justify-center gap-4 flex-wrap">
          <Ana size={130} />
          <Carlos size={130} waving />
          <Mascot size={110} />
        </div>
      )}
      <div className="mt-8 text-xs font-body opacity-90">
        {lang === "es"
          ? "Pasa la página con \"Siguiente\" o la flecha derecha →"
          : "Turn the page with \"Next\" or the right arrow →"}
      </div>
    </div>
  </div>
);

/* ─────────── Overlay speech bubble that sits on top of the AI art ─────────── */
const BUBBLE_TONE = {
  ana: "#eaf3ff",
  carlos: "#fff0e6",
  mascot: "#fff8d6",
  narrator: "#ffffff",
};
const SPEAKER_LABEL = {
  ana: "ANA",
  carlos: "CARLOS",
  mascot: "SOFY",
};

const OverlayBubble = ({ speaker, text, action, lang, tailSide = "left", delay = 0, compact = false }) => {
  const name = SPEAKER_LABEL[speaker];
  const tone = BUBBLE_TONE[speaker] || "#fff";
  const padCls = compact ? "px-2.5 py-2 sm:px-3 sm:py-2.5" : "px-4 py-3 sm:px-5 sm:py-4";
  const textCls = compact ? "text-xs sm:text-sm leading-tight" : "text-base sm:text-lg leading-snug";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 18 }}
      className="relative"
    >
      <div
        className={`brutal font-display font-bold ${padCls} ${textCls}`}
        style={{ background: tone }}
      >
        {name && (
          <div
            className={`absolute -top-3 brutal-sm bg-canales-yellow text-black px-2 py-0.5 text-[10px] font-black tracking-widest`}
            style={tailSide === "right" ? { right: 10 } : { left: 10 }}
          >
            {name}
          </div>
        )}
        <div>{text}</div>
        {action && (
          <div className="mt-1.5 pt-1.5 border-t-2 border-black/20 text-xs italic font-body font-normal">
            {action[lang]}
          </div>
        )}
      </div>
      {/* tail */}
      <div
        className={`absolute w-0 h-0 ${tailSide === "right" ? "right-6" : "left-6"}`}
        style={{
          bottom: -14,
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderTop: "16px solid #000",
        }}
      />
      <div
        className={`absolute w-0 h-0 ${tailSide === "right" ? "right-7" : "left-7"}`}
        style={{
          bottom: -10,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: `12px solid ${tone}`,
        }}
      />
    </motion.div>
  );
};

/* Fixed positions per (# of dialogue bubbles, index) that read left-to-right,
   top-to-bottom like real comics. Values are % of the image container. */
const OVERLAY_LAYOUTS = {
  1: [{ top: "5%", left: "3%", maxWidth: "42%", tail: "left" }],
  2: [
    { top: "4%", left: "3%", maxWidth: "38%", tail: "left" },
    { top: "4%", right: "3%", maxWidth: "38%", tail: "right" },
  ],
  3: [
    { top: "4%", left: "3%", maxWidth: "34%", tail: "left" },
    { top: "4%", right: "3%", maxWidth: "34%", tail: "right" },
    { bottom: "16%", left: "3%", maxWidth: "36%", tail: "left" },
  ],
};

/* Scene bubble corner layout — rotates through 4 corners so bubbles never
   sit centered over characters' faces. Kept intentionally compact so most of
   the AI illustration stays visible. */
const SCENE_BUBBLE_CORNERS = [
  { top: "6%", left: "4%", maxWidth: "44%", tail: "left" },
  { top: "6%", right: "4%", maxWidth: "44%", tail: "right" },
  { top: "6%", left: "4%", maxWidth: "44%", tail: "left" },
  { top: "6%", right: "4%", maxWidth: "44%", tail: "right" },
  { top: "6%", left: "4%", maxWidth: "44%", tail: "left" },
  { top: "6%", right: "4%", maxWidth: "44%", tail: "right" },
];

/* Small red numbered chip that lives on the top-left of every scene panel —
   matches the print-comic style ("1", "2", "3"…) used in Canales flyers. */
const SceneNumber = ({ n }) => (
  <div
    className="absolute z-20 flex items-center justify-center font-display font-black"
    style={{
      top: 10,
      left: 10,
      width: 46,
      height: 46,
      background: "linear-gradient(180deg,#E3000F 0%,#A50009 100%)",
      color: "white",
      border: "4px solid #000",
      boxShadow: "4px 4px 0 #000, inset -3px -3px 0 rgba(0,0,0,0.25)",
      fontSize: 28,
      lineHeight: 1,
    }}
    data-testid={`comic-scene-number-${n}`}
  >
    {n}
  </div>
);

/* Trim long dialogue to a very short quote so scene bubbles stay 1-2 lines
   and never mushroom into a tall block that hides the character's face. */
const SHORT_LEN = 70;
const shortenBubble = (t) => {
  if (!t) return "";
  const s = String(t).trim();
  if (s.length <= SHORT_LEN) return s;
  const cut = s.slice(0, SHORT_LEN);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut) + "…";
};

/* ─────────── Single scene panel with its own AI image + speech bubble ─────────── */
const ScenePanel = ({ scene, illustration, lang, index, isFinal }) => {
  const panel = scene.panel;
  const tail = panel?.tail || (index % 2 === 0 ? "left" : "right");
  return (
    <div
      className="brutal-sm bg-white overflow-hidden relative flex flex-col"
      data-testid={`comic-scene-${index}`}
      style={
        isFinal
          ? {
              backgroundImage:
                "radial-gradient(circle at center, #FFF3B0 0%, #FFF9DB 55%, #ffffff 100%)",
            }
          : undefined
      }
    >
      <SceneNumber n={index + 1} />
      {/* Yellow narration caption ABOVE the art (never on top of faces). */}
      {scene.caption && (
        <div
          className="px-3 py-1.5 border-b-4 border-black"
          style={{ background: "#FFF3B0", marginLeft: 56 }}
        >
          <div className="font-display font-bold text-xs sm:text-sm leading-tight">
            {scene.caption[lang]}
          </div>
        </div>
      )}
      {/* AI illustration — faces stay COMPLETELY unobstructed. */}
      <div className="relative">
        {illustration ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/illustrations/${illustration.key}`}
            alt=""
            className="w-full block"
            style={{ aspectRatio: "4 / 3", objectFit: "cover" }}
          />
        ) : (
          <div
            className="w-full flex items-center justify-center bg-halftone-blue gap-2"
            style={{ aspectRatio: "4 / 3" }}
          >
            <Ana size={70} /><Carlos size={70} waving /><Mascot size={60} />
          </div>
        )}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none mix-blend-multiply"
          style={{
            backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
            backgroundSize: "10px 10px",
          }}
        />
      </div>
      {/* Speech bubble BELOW the art in its own strip — faces never covered. */}
      {panel && (
        <div
          className="px-3 py-2 border-t-4 border-black"
          style={{ background: "#FFFEF6" }}
          data-testid={`comic-scene-bubble-${index}`}
        >
          <div className="flex items-start gap-2">
            {SPEAKER_LABEL[panel.speaker] && (
              <span
                className="shrink-0 brutal-sm bg-canales-yellow text-black px-1.5 py-0.5 text-[10px] font-display font-black tracking-widest"
              >
                {SPEAKER_LABEL[panel.speaker]}
              </span>
            )}
            <div className="font-display font-bold text-xs sm:text-sm leading-tight">
              {shortenBubble(panel.text[lang])}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────── Regular story page — bubbles overlaid on AI art ─────────── */
const PageBody = ({ page, lang, illustration, scenesIllus = {}, quiz, onQuizAnswered, pageNumber, totalPages }) => {
  const allPanels = page.panels || [];
  const dialogues = allPanels.filter((p) => p.speaker && p.speaker !== "narrator");
  const posList =
    OVERLAY_LAYOUTS[Math.min(dialogues.length, 3)] || OVERLAY_LAYOUTS[2];

  // If page has scenes (explicit OR auto-derived from panels), render multi-panel.
  // Every non-cover page must show at LEAST 3 scenes — pad with reaction shots
  // from the derived list (or synthesize placeholders) so the reader always
  // gets a full comic-book grid.
  const explicitScenes = Array.isArray(page.scenes) ? page.scenes : [];
  const derived = deriveScenes(
    { category: page._category, title: page._title || { es: "", en: "" } },
    page,
    0
  );
  let effectiveScenes = explicitScenes.length > 0
    ? [...explicitScenes, ...derived.slice(explicitScenes.length)]
    : [...derived];
  // Final guarantee: pad with reaction placeholders so pages ALWAYS render 6 panels
  // (2×3 grid) matching the printed Canales flyer layout. Uses the page caption or
  // recycles the last existing panel's dialogue so the extra tiles still feel intentional.
  const TARGET_PANELS = 6;
  while (effectiveScenes.length > 0 && effectiveScenes.length < TARGET_PANELS) {
    effectiveScenes.push({
      auto: true,
      panel: null,
      caption: page.caption || null,
      _pad: true,
    });
  }
  if (effectiveScenes.length >= 2) {
    const scenes = effectiveScenes;
    // ALWAYS 2 columns × N rows — matches the printed 6-panel Canales flyer layout.
    const cols = "grid-cols-2";
    const titleText = (page.title?.[lang] || "").trim();
    const titleWords = titleText.split(/\s+/).filter(Boolean);
    // Split into 2 lines matching the print-flyer look (~half/half).
    const midIdx = Math.ceil(titleWords.length / 2);
    const line1 = titleWords.slice(0, midIdx).join(" ");
    const line2 = titleWords.slice(midIdx).join(" ");
    return (
      // Letter-size sheet (8.5" × 11"). Uses minHeight (not fixed aspect-ratio)
      // so if the 6-panel content is a bit taller, the PÁGINA N DE M banner is
      // still visible at the bottom instead of being clipped.
      <div
        className="brutal p-3 sm:p-5 bg-white space-y-3 mx-auto"
        style={{
          maxWidth: "min(100%, 850px)",
          minHeight: "min(90vh, 1100px)",
          boxShadow: "10px 10px 0 rgba(0,0,0,0.15)",
        }}
        data-testid="comic-letter-sheet"
      >
        {titleText && (
          <div className="relative pt-6 pb-3">
            {/* explosion rays behind the banner — matches the flyer 'BAM' energy */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-conic-gradient(from 0deg at 50% 50%, rgba(255,209,0,0.35) 0deg 8deg, transparent 8deg 20deg)",
                filter: "blur(0.5px)",
                zIndex: 0,
              }}
            />
            {/* dramatic red banner — 2 lines, yellow top / white bottom, big shadow */}
            <div
              className="relative text-center px-6 py-4 sm:py-5 border-4 border-black font-display font-black uppercase z-10"
              style={{
                backgroundColor: "#E3000F",
                backgroundImage:
                  "linear-gradient(180deg,#E3000F 0%,#9E0009 100%)",
                color: "#ffffff",
                boxShadow: "8px 8px 0 #000, inset 0 -6px 0 rgba(0,0,0,0.28)",
                textShadow: "3px 3px 0 rgba(0,0,0,0.4)",
                letterSpacing: "0.02em",
                transform: "skewX(-2deg)",
              }}
              data-testid="comic-page-banner"
            >
              <div style={{ transform: "skewX(2deg)" }}>
                <div
                  style={{
                    color: "#FFD100",
                    fontSize: "clamp(1.5rem,3.5vw,2.8rem)",
                    lineHeight: 1,
                    WebkitTextStroke: "1.5px #000",
                  }}
                >
                  {line1}
                </div>
                {line2 && (
                  <div
                    style={{
                      color: "#ffffff",
                      fontSize: "clamp(1.25rem,2.8vw,2.2rem)",
                      lineHeight: 1.05,
                      marginTop: 4,
                      WebkitTextStroke: "1px #000",
                    }}
                  >
                    {line2}
                  </div>
                )}
              </div>
            </div>
            <div
              className="absolute -top-1 -left-4 leading-none z-20"
              style={{ fontSize: 52, color: "#FFD100", textShadow: "3px 3px 0 #000, -1px -1px 0 #000" }}
            >
              ★
            </div>
            <div
              className="absolute -top-1 -right-4 leading-none z-20"
              style={{ fontSize: 52, color: "#FFD100", textShadow: "3px 3px 0 #000, -1px -1px 0 #000" }}
            >
              ★
            </div>
          </div>
        )}
        {page.caption && (
          <div
            className="brutal-sm px-4 py-2.5 text-center font-body font-black text-sm sm:text-base"
            style={{ background: "#FFF3B0", borderColor: "#000" }}
          >
            <span style={{ color: "#E3000F", fontWeight: 900 }}>➞</span>{" "}
            {page.caption[lang]}{" "}
            <span style={{ color: "#E3000F", fontWeight: 900 }}>⇐</span>
          </div>
        )}
        {/* Fallback: if a scene has no dedicated illustration, cycle through any
            existing scene illustrations (or the hero) so all 6 tiles ALWAYS show
            real AI art instead of the placeholder icons. */}
        {(() => {
          const available = [];
          for (let k = 1; k <= 6; k++) {
            if (scenesIllus[k]) available.push(scenesIllus[k]);
          }
          if (illustration) available.push(illustration);
          const pickIllus = (i) => {
            const own = scenesIllus[i + 1] || (i === 0 ? illustration : undefined);
            if (own) return own;
            if (available.length === 0) return undefined;
            return available[i % available.length];
          };
          return (
            <div className={`grid gap-3 ${cols}`}>
              {scenes.map((sc, i) => (
                <ScenePanel
                  key={i}
                  scene={sc}
                  illustration={pickIllus(i)}
                  lang={lang}
                  index={i}
                  isFinal={i === scenes.length - 1}
                />
              ))}
            </div>
          );
        })()}
        {page.cta && (
          <div className="brutal p-3 bg-canales-yellow text-black font-display font-bold text-sm sm:text-base">
            {page.cta[lang]}
          </div>
        )}
        {quiz && <InteractiveBeat quiz={quiz} lang={lang} onAnswered={onQuizAnswered} />}
        {page.lesson && (
          <FactBox variant={page.lesson.variant} title={page.lesson.title[lang]}>
            {page.lesson.text[lang]}
          </FactBox>
        )}
        {/* "PÁGINA N DE M" red closing banner (matches the printed flyer layout) */}
        {pageNumber && totalPages && (
          <div className="flex justify-center pt-1">
            <div
              className="px-6 py-1.5 border-4 border-black font-display font-black text-white uppercase tracking-widest"
              style={{
                backgroundColor: "#E3000F",
                backgroundImage: "linear-gradient(180deg,#E3000F 0%,#A50009 100%)",
                boxShadow: "5px 5px 0 #000",
                fontSize: "clamp(0.85rem,1.6vw,1.1rem)",
                textShadow: "2px 2px 0 rgba(0,0,0,0.35)",
              }}
              data-testid="comic-page-footer"
            >
              {lang === "es" ? `PÁGINA ${pageNumber} DE ${totalPages}` : `PAGE ${pageNumber} OF ${totalPages}`}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="brutal p-3 sm:p-4 bg-canales-cream space-y-4">
      {/* Immersive comic panel: AI art + overlayed speech bubbles + narrator strip */}
      <div
        className="relative brutal-sm overflow-hidden bg-white mx-auto"
        style={{ maxWidth: "min(100%, 720px)" }}
        data-testid="comic-page-hero"
      >
        {illustration ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/illustrations/${illustration.key}`}
            alt={page.title?.[lang] || ""}
            className="w-full block"
            style={{ aspectRatio: "4 / 3", objectFit: "cover" }}
          />
        ) : (
          <div
            className="w-full bg-halftone-blue flex items-center justify-center gap-3"
            style={{ aspectRatio: "4 / 3" }}
          >
            <Ana size={110} />
            <Carlos size={110} waving />
            <Mascot size={90} />
          </div>
        )}

        {/* subtle halftone overlay */}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none mix-blend-multiply"
          style={{
            backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        />

        {/* Chapter badge */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 brutal-sm bg-canales-red text-white px-2.5 py-1 sm:px-3 sm:py-1.5 font-display font-black text-xs sm:text-sm uppercase tracking-widest z-10">
          {page.title?.[lang]}
        </div>

        {/* Speech bubbles — absolutely positioned on desktop, hidden on mobile */}
        <div className="hidden md:block absolute inset-0 pointer-events-none">
          {dialogues.map((p, i) => {
            const pos = posList[i] || posList[posList.length - 1];
            const { tail, ...cssPos } = pos;
            return (
              <div
                key={i}
                className="absolute pointer-events-auto"
                style={cssPos}
                data-testid={`comic-overlay-bubble-${i}`}
              >
                <OverlayBubble
                  speaker={p.speaker}
                  text={p.text[lang]}
                  action={p.action}
                  lang={lang}
                  tailSide={tail}
                  delay={0.15 + i * 0.15}
                />
              </div>
            );
          })}
        </div>

        {/* Narrator caption — bottom strip on the image (classic comic style) */}
        {page.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-white/95 border-t-4 border-black px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="font-body italic text-base sm:text-lg leading-snug">
              {page.caption[lang]}
            </div>
          </div>
        )}
      </div>

      {/* Mobile: bubbles stacked below the image */}
      <div className="md:hidden space-y-3">
        {dialogues.map((p, i) => (
          <OverlayBubble
            key={i}
            speaker={p.speaker}
            text={p.text[lang]}
            action={p.action}
            lang={lang}
            tailSide={i % 2 === 0 ? "left" : "right"}
          />
        ))}
      </div>

      {/* CTA on final-story pages */}
      {page.cta && (
        <div className="brutal p-4 bg-canales-yellow text-black font-display font-bold text-base sm:text-lg">
          {page.cta[lang]}
        </div>
      )}

      {/* Interactive quiz */}
      {quiz && <InteractiveBeat quiz={quiz} lang={lang} onAnswered={onQuizAnswered} />}

      {/* Lesson callout */}
      {page.lesson && (
        <FactBox variant={page.lesson.variant} title={page.lesson.title[lang]}>
          {page.lesson.text[lang]}
        </FactBox>
      )}
    </div>
  );
};

/* ─────────── Main reader ─────────── */
export default function ComicReader({ embedded = false, previewSlug = null, previewOverrides = null }) {
  const params = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const slug = embedded ? previewSlug : params.slug;
  const comic = useMemo(() => findComic(slug), [slug]);

  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [illustrations, setIllustrations] = useState({}); // {[page]: {[scene]: item}}
  const [overrides, setOverrides] = useState({});
  const [sfx, setSfx] = useState(null);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(0);
  const sfxTimer = useRef(null);

  // Fetch admin content overrides (titles / captions / dialogues in Mongo)
  useEffect(() => {
    if (embedded) {
      setOverrides(previewOverrides || {});
      return;
    }
    (async () => {
      if (!comic) return;
      try {
        const { data } = await api.get(`/comics/${comic.slug}/content`);
        setOverrides(data?.overrides || {});
      } catch {
        setOverrides({});
      }
    })();
  }, [comic, embedded, previewOverrides]);

  // Apply per-page overrides to base data
  const effectiveComic = useMemo(() => {
    if (!comic) return null;
    const applied = { ...comic, pages: comic.pages.map((p, i) => {
      const ov = overrides[i];
      if (!ov) return p;
      const next = { ...p };
      if (ov.title_es || ov.title_en) {
        next.title = { es: ov.title_es ?? p.title?.es, en: ov.title_en ?? p.title?.en };
      }
      if (ov.caption_es || ov.caption_en) {
        next.caption = { es: ov.caption_es ?? p.caption?.es, en: ov.caption_en ?? p.caption?.en };
      }
      if (Array.isArray(ov.panels) && p.panels) {
        next.panels = p.panels.map((pp, pi) => {
          const opp = ov.panels?.[pi];
          if (!opp) return pp;
          return {
            ...pp,
            text: {
              es: opp.text_es ?? pp.text?.es,
              en: opp.text_en ?? pp.text?.en,
            },
          };
        });
      }
      if (Array.isArray(ov.scenes) && p.scenes) {
        next.scenes = p.scenes.map((sc, si) => {
          const osc = ov.scenes?.[si];
          if (!osc) return sc;
          const next2 = { ...sc };
          if (osc.caption_es || osc.caption_en) {
            next2.caption = { es: osc.caption_es ?? sc.caption?.es, en: osc.caption_en ?? sc.caption?.en };
          }
          return next2;
        });
      }
      return next;
    }) };
    return applied;
  }, [comic, overrides]);

  // Fetch AI illustrations for this comic — build a map keyed by page & scene.
  useEffect(() => {
    (async () => {
      if (!comic) return;
      try {
        const { data } = await api.get(`/comics/${comic.slug}/illustrations`);
        const map = {};
        for (const it of data) {
          const p = it.page_index || 0;
          const s = it.scene_index || 0;
          if (!map[p]) map[p] = {};
          map[p][s] = it;
        }
        setIllustrations(map);
      } catch {
        setIllustrations({});
      }
    })();
  }, [comic]);

  // Keyboard nav
  useEffect(() => {
    if (embedded) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") {
        setDir(1);
        setIdx((i) => Math.min((comic?.pages.length || 1) - 1, i + 1));
      }
      if (e.key === "ArrowLeft") {
        setDir(-1);
        setIdx((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [comic, embedded]);

  // SFX on page turn
  useEffect(() => {
    if (idx === 0) return;
    const w = SFX_WORDS[Math.floor(Math.random() * SFX_WORDS.length)];
    setSfx(w);
    clearTimeout(sfxTimer.current);
    sfxTimer.current = setTimeout(() => setSfx(null), 1100);
    return () => clearTimeout(sfxTimer.current);
  }, [idx]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-halftone">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="brutal p-8 bg-white">
            <div className="font-display font-black text-3xl">404</div>
            <p className="mt-2 font-body">
              {lang === "es" ? "Cómic no encontrado." : "Comic not found."}
            </p>
            <Link to="/comics" className="mt-4 inline-flex brutal-btn bg-canales-blue text-white px-4 py-2 gap-2">
              <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Volver" : "Back"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const activeComic = effectiveComic || comic;
  const total = activeComic.pages.length;
  const page = activeComic.pages[idx];
  const isCover = page.layout === "cover";
  const isLast = idx === total - 1;
  const quiz = !isCover ? getInteractive(comic.slug, idx) : null;
  const pageIllus = illustrations[idx] || {};
  const illustration = pageIllus[0]; // primary AI image (backward-compat)

  const next = () => {
    setDir(1);
    setIdx((i) => Math.min(total - 1, i + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setDir(-1);
    setIdx((i) => Math.max(0, i - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goExam = () => navigate(`/exam/${comic.exam_slug}`);
  const restart = () => {
    setDir(-1);
    setIdx(0);
    setScore(0);
    setAnswered(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onQuizAnswered = (correct) => {
    setAnswered((a) => a + 1);
    if (correct) setScore((s) => s + 1);
  };

  return (
    <div className={embedded ? "bg-halftone-blue" : "min-h-screen bg-halftone-blue"} data-testid="comic-reader">
      {!embedded && <Navbar />}
      <div className={embedded ? "max-w-5xl mx-auto px-2 py-4" : "max-w-5xl mx-auto px-4 py-8"}>
        {/* Header row */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          {!embedded && (
            <Link
              to="/comics"
              data-testid="comic-back"
              className="brutal-btn bg-white text-black px-3 py-1 text-sm flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Cómics" : "Comics"}
            </Link>
          )}
          <div className="flex-1 min-w-[120px] h-3 bg-white border-2 border-black rounded-full overflow-hidden">
            <div
              className="h-full bg-canales-red transition-all"
              style={{ width: `${((idx + 1) / total) * 100}%` }}
            />
          </div>
          <span className="font-display font-bold text-sm" data-testid="comic-page-indicator">
            {idx + 1} / {total}
          </span>
          {answered > 0 && (
            <span
              data-testid="comic-quiz-score"
              className="brutal-sm bg-canales-yellow px-2 py-1 text-xs font-display font-black"
            >
              ⭐ {score}/{answered}
            </span>
          )}
        </div>

        {/* Page dots */}
        <div className="flex flex-wrap gap-1 mb-6">
          {comic.pages.map((p, i) => (
            <button
              key={i}
              onClick={() => {
                setDir(i > idx ? 1 : -1);
                setIdx(i);
              }}
              data-testid={`comic-dot-${i}`}
              aria-label={`page ${i + 1}`}
              className={`h-3 w-6 rounded-full border-2 border-black ${
                i === idx ? "bg-canales-red" : "bg-white"
              }`}
            />
          ))}
        </div>

        {/* Animated page */}
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              initial={{ opacity: 0, x: dir > 0 ? 80 : -80, rotateY: dir > 0 ? 12 : -12 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              exit={{ opacity: 0, x: dir > 0 ? -80 : 80, rotateY: dir > 0 ? -12 : 12 }}
              transition={{ duration: 0.35 }}
              className="relative"
            >
              <AnimatePresence>
                {sfx && <SFX word={sfx} color={comic.accent} />}
              </AnimatePresence>
              {isCover ? (
                <PageCover comic={comic} lang={lang} illustration={illustration} />
              ) : (
                <PageBody
                  page={page}
                  lang={lang}
                  illustration={illustration}
                  scenesIllus={pageIllus}
                  quiz={quiz}
                  onQuizAnswered={onQuizAnswered}
                  pageNumber={idx + 1}
                  totalPages={total}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Digital badge — appears on the last page */}
        {isLast && (
          <div className="mt-6">
            <ComicBadge comic={activeComic} lang={lang} testid="comic-badge" />
          </div>
        )}

        {/* Training materials reinforcement (last page only) */}
        {!embedded && isLast && materialsForComic(comic.slug).length > 0 && (
          <div className="mt-6 brutal p-5 bg-canales-cream" data-testid="comic-training-materials">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Library className="w-6 h-6" />
              <h3 className="font-display font-black text-xl">
                {lang === "es" ? "Refuerza con el material oficial" : "Reinforce with official material"}
              </h3>
              <Link
                to="/biblioteca"
                className="ml-auto brutal-btn bg-white px-2 py-1 text-xs font-display font-bold inline-flex items-center gap-1"
                data-testid="comic-open-library"
              >
                <BookOpen className="w-3.5 h-3.5" />
                {lang === "es" ? "Ver biblioteca" : "Open library"}
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {materialsForComic(comic.slug).map((m, i) => {
                const Icon = m.type === "video" ? Play : FileText;
                return (
                  <a
                    key={i}
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`comic-material-${m.type}-${i}`}
                    className="brutal-sm bg-white p-3 flex items-start gap-3 hover:bg-canales-yellow transition-colors"
                  >
                    <div className="shrink-0 w-10 h-10 flex items-center justify-center border-2 border-black bg-black text-white">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-black text-sm leading-tight">
                        {m.title[lang] || m.title.es}
                      </div>
                      <div className="text-[10px] font-body text-neutral-600 uppercase tracking-widest mt-0.5">
                        {m.type === "pdf" ? "PDF" : "VIDEO"} · {m.lang.toUpperCase()}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 flex flex-wrap justify-between gap-3">
          <button
            onClick={prev}
            disabled={idx === 0}
            data-testid="comic-prev"
            className="brutal-btn bg-white px-4 py-2 disabled:opacity-40 flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> {lang === "es" ? "Anterior" : "Previous"}
          </button>
          {isLast ? (
            <div className="flex flex-wrap gap-2 items-center">
              {!embedded && (
                <ShareComic
                  title={comic.title[lang]}
                  url={typeof window !== "undefined" ? window.location.href : `/comics/${comic.slug}`}
                  message={lang === "es"
                    ? `¡Lee este cómic de Canales! "${comic.title[lang]}" — historia + examen para ganar más dinero.`
                    : `Read this Canales comic! "${comic.title[lang]}" — story + exam to earn more.`}
                  testid="comic-share"
                />
              )}
              {!embedded && <PdfComicButton comic={comic} lang={lang} testid="comic-pdf-btn" />}
              <button
                onClick={restart}
                data-testid="comic-restart"
                className="brutal-btn bg-white text-black px-4 py-2 flex items-center gap-2"
              >
                <RefreshCcw className="w-4 h-4" /> {lang === "es" ? "Releer" : "Re-read"}
              </button>
              {!embedded && (
                <button
                  onClick={goExam}
                  data-testid="comic-goto-exam"
                  className="brutal-btn bg-canales-red text-white px-5 py-2 flex items-center gap-2 font-display font-black"
                >
                  <ClipboardCheck className="w-5 h-5" />
                  {lang === "es" ? "Tomar el examen" : "Take the exam"}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={next}
              data-testid="comic-next"
              className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2 font-display font-black"
            >
              {lang === "es" ? "Siguiente página" : "Next page"} <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="mt-6 text-xs text-center font-body text-neutral-600">
          <BookOpen className="w-4 h-4 inline-block mr-1" />
          {lang === "es"
            ? "Tip: usa las flechas ← → del teclado y responde \"¿Y ahora tú qué harías?\" para subir tu puntaje."
            : "Tip: use ← → arrow keys and answer the \"What would YOU do?\" beat to boost your score."}
        </div>
      </div>
    </div>
  );
}
